'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Loader2, MessageSquare, Plus, Search, TriangleAlert } from 'lucide-react';
import { getMisSolicitudes, getSolicitudesDeInvestigador, getUnidadesCatalogo } from '@/lib/solicitudes';
import { ESTADO_SOLICITUD_CONFIG, TIPO_SOLICITUD_LABELS } from '@/data/solicitudesData';
import type { SolicitudResponse } from '@siiv/schemas';
import { TipoSolicitudSelectorModal } from '../solicitudes/TipoSolicitudSelectorModal';
import { getSolicitudTipo, type TipoSolicitudCreable } from '../solicitudes/solicitudTipos';
import { claveEstadoVisible } from '../solicitudes/estadoVisible';
import { nombresDeUnidades, type UnidadCatalogoMin } from '../solicitudes/adscripcion/adscripcionModel';
import { resultadoItems } from '../solicitudes/adscripcion/adscripcionPreview';
import { ItemsDeAdscripcion } from '../solicitudes/adscripcion/ItemsDeAdscripcion';
import { TextoDeCambio } from '../solicitudes/adscripcion/TextoDeCambio';

function formatFecha(isoDate: string) {
	return new Intl.DateTimeFormat('es-CL', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	}).format(new Date(isoDate));
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

interface MisSolicitudesTabProps {
	/**
	 * Perfil AJENO cuyo historial se muestra (R63). Sin él, la lista es la del
	 * usuario en sesión — el caso de siempre.
	 *
	 * Con él la vista pierde el botón de crear, y no por prolijidad: una solicitud
	 * la firma quien la necesita. Un administrador que la creara desde acá dejaría
	 * el registro a nombre de la persona equivocada.
	 */
	slug?: string;
}

function ModalDeTipo({
	tipo,
	onClose,
	onSolicitudCreada,
}: {
	tipo: TipoSolicitudCreable;
	onClose: () => void;
	onSolicitudCreada: () => void;
}) {
	const entry = getSolicitudTipo(tipo);
	if (!entry) return null;
	const { Modal, maxWidth } = entry;
	return <Modal onClose={onClose} onSolicitudCreada={onSolicitudCreada} maxWidth={maxWidth} />;
}

export function MisSolicitudesTab({ slug }: MisSolicitudesTabProps) {
	const ajeno = Boolean(slug);
	const [solicitudes, setSolicitudes] = useState<SolicitudResponse[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	// Unit names for the items of an adscription request; fetched once, only when a card needs them.
	const [catalogo, setCatalogo] = useState<UnidadCatalogoMin[]>([]);
	const [search, setSearch] = useState('');
	// "Nueva solicitud" first opens the type selector (always visible); choosing a type swaps it for that
	// type's own modal, opened at the size the registry gives it.
	const [selectorAbierto, setSelectorAbierto] = useState(false);
	const [tipoElegido, setTipoElegido] = useState<TipoSolicitudCreable | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = slug ? await getSolicitudesDeInvestigador(slug) : await getMisSolicitudes();
			setSolicitudes(data.solicitudes);
		} catch {
			setError(
				slug
					? 'No pudimos cargar las solicitudes de esta persona. Reintenta en unos segundos.'
					: 'No pudimos cargar tus solicitudes. Reintenta en unos segundos.',
			);
		} finally {
			setLoading(false);
		}
	}, [slug]);

	useEffect(() => {
		load();
	}, [load]);

	const hayAdscripcion = solicitudes.some((s) => s.adscripcion !== null);
	useEffect(() => {
		if (!hayAdscripcion) return;
		let activo = true;
		getUnidadesCatalogo()
			.then((res) => {
				if (activo) setCatalogo(res.unidades);
			})
			.catch(() => {
				// Not critical: the items fall back to "Unidad N".
			});
		return () => {
			activo = false;
		};
	}, [hayAdscripcion]);

	// Items of every adscription card, built once per list/catalog (not per card on each keystroke of the search).
	const itemsPorSolicitud = useMemo(() => {
		const porId = new Map<number, ReturnType<typeof resultadoItems>>();
		for (const s of solicitudes) {
			if (s.adscripcion) {
				porId.set(s.idSolicitud, resultadoItems(s.adscripcion, nombresDeUnidades(catalogo, s.adscripcion.nombreOtra)));
			}
		}
		return porId;
	}, [solicitudes, catalogo]);
	// A request whose changes carry their own reason shows it under each change; the separate "Justificación" block
	// (the same reasons joined into one text) would only repeat them. Old requests keep the block.
	const justificadaPorCambio = (idSolicitud: number) =>
		(itemsPorSolicitud.get(idSolicitud) ?? []).some((item) => item.motivoSolicitud);

	const filtered = useMemo(() => {
		const q = norm(search.trim());
		if (!q) return solicitudes;
		return solicitudes.filter((s) => {
			const tipo = TIPO_SOLICITUD_LABELS[s.tipoSolicitud] ?? s.tipoSolicitud;
			const estado = ESTADO_SOLICITUD_CONFIG[claveEstadoVisible(s)]?.label ?? s.estado;
			// Barre por todo el contenido visible del elemento: título, tipo, tag de
			// estado, la justificación de la persona, la respuesta del admin, el #id y
			// la fecha formateada.
			return norm(
				[
					s.titulo,
					tipo,
					estado,
					s.estado,
					s.descripcion ?? '',
					s.respuestaAdmin ?? '',
					`#${s.idSolicitud}`,
					formatFecha(s.createdAt),
				].join(' '),
			).includes(q);
		});
	}, [solicitudes, search]);

	return (
		<div>
			{/* ── Barra: buscador + Nueva solicitud ──
			    Apilados en el teléfono (`sm:` devuelve la fila a partir de 640px, el mismo
			    corte que usan las hojas del perfil): compartiendo fila, el campo quedaba en
			    unos 120px y no entraba ni el texto del placeholder. */}
			<div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
				<span className="flex flex-1 items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-3 py-2">
					<Search className="h-4 w-4 text-slate-500" />
					<input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={ajeno ? 'Buscar en sus solicitudes' : 'Buscar en tus solicitudes'}
						autoComplete="off"
						name="mis-solicitudes-search"
						className="w-full border-0 bg-transparent p-0 text-sm text-slate-700 outline-none"
					/>
				</span>
				{ajeno ? null : (
					<button
						type="button"
						onClick={() => setSelectorAbierto(true)}
						className="inline-flex w-full shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] bg-[#233a4d] px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-[#1b2d3b] sm:w-auto sm:justify-start"
					>
						<Plus size={16} /> Nueva solicitud
					</button>
				)}
			</div>

			{/* ── Contenido ── */}
			{loading ? (
				<div className="flex h-40 items-center justify-center gap-2 text-sm text-slate-500">
					<Loader2 size={16} className="animate-spin" />{' '}
					{ajeno ? 'Cargando sus solicitudes…' : 'Cargando tus solicitudes…'}
				</div>
			) : error ? (
				<div className="flex items-center gap-2 rounded-[10px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
					<TriangleAlert size={16} /> {error}
				</div>
			) : solicitudes.length === 0 ? (
				<div className="flex flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
					<Inbox size={22} className="text-slate-400" />
					{ajeno
						? 'Esta persona todavía no ha enviado ninguna solicitud.'
						: 'Aún no tienes solicitudes. Usa "Nueva solicitud" para enviar una.'}
				</div>
			) : filtered.length === 0 ? (
				<div className="rounded-[10px] border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
					No encontramos solicitudes con esa búsqueda.
				</div>
			) : (
				<div className="flex flex-col gap-3">
					{filtered.map((s) => {
						// A rejected request that a newer one replaced is shown as "Reemplazada" (grey), never as a refusal.
						const estado = ESTADO_SOLICITUD_CONFIG[claveEstadoVisible(s)] ?? {
							label: s.estado,
							color: 'text-slate-700',
							bg: 'bg-slate-100 border-slate-200',
						};

						return (
							<article
								key={s.idSolicitud}
								className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm"
							>
								{/* El chip lleva `nowrap` y no se comprime: en una tarjeta angosta se llevaba
								    medio ancho y el título salía cortado de a una palabra por renglón. Bajo
								    640px se va a su propia línea, arriba del título. */}
								<div className="flex flex-col-reverse items-start gap-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
									<p className="font-semibold text-slate-800">{s.titulo}</p>
									<span
										className={`inline-flex shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${estado.color} ${estado.bg}`}
									>
										{estado.label}
									</span>
								</div>

								<p className="mt-1.5 text-xs text-slate-500">{formatFecha(s.createdAt)}</p>

								{s.adscripcion ? (
									<ItemsDeAdscripcion
										items={itemsPorSolicitud.get(s.idSolicitud) ?? []}
										conMotivos
									/>
								) : null}

								{(s.descripcion && !justificadaPorCambio(s.idSolicitud)) || s.respuestaAdmin ? (
									<div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
										{s.descripcion && !justificadaPorCambio(s.idSolicitud) ? <Justificacion texto={s.descripcion} /> : null}
										{s.respuestaAdmin ? (
											// A comment from the admin, not one more change: message icon and a dimmed author.
											<p className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
												<MessageSquare size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
												<span>
													<span className="font-medium text-slate-500">Administrador ·</span> {s.respuestaAdmin}
												</span>
											</p>
										) : null}
									</div>
								) : null}
							</article>
						);
					})}
				</div>
			)}

			{selectorAbierto && !ajeno ? (
				<TipoSolicitudSelectorModal
					onClose={() => setSelectorAbierto(false)}
					onSelect={(tipo) => {
						setSelectorAbierto(false);
						setTipoElegido(tipo);
					}}
				/>
			) : null}

			{tipoElegido && !ajeno ? <ModalDeTipo tipo={tipoElegido} onClose={() => setTipoElegido(null)} onSolicitudCreada={load} /> : null}
		</div>
	);
}

/**
 * The researcher's reason. One line reads inline after the label; several (one per change of an adscription
 * request) go below it as bullets. Each line is shown like the change it explains (`TextoDeCambio`).
 */
function Justificacion({ texto }: { texto: string }) {
	const lineas = texto
		.split('\n')
		.map((linea) => linea.trim())
		.filter((linea) => linea !== '');
	const rotulo = <span className="font-semibold text-[#233a4d]">Justificación:</span>;

	if (lineas.length <= 1) {
		return (
			<p className="text-xs leading-relaxed text-slate-700">
				{rotulo} <TextoDeCambio texto={lineas[0] ?? texto} />
			</p>
		);
	}
	return (
		<div className="text-xs leading-relaxed text-slate-700">
			<p>{rotulo}</p>
			<ul className="mt-1 list-disc space-y-0.5 pl-5 marker:text-slate-400">
				{lineas.map((linea, indice) => (
					<li key={indice}>
						<TextoDeCambio texto={linea} />
					</li>
				))}
			</ul>
		</div>
	);
}
